import Gradient from "javascript-color-gradient";

export const toFixedFloatDown = (num: number, digits: number): string => {
  const isNumberNegative = num < 0;
  num = Math.abs(num);
  const factor = 10 ** digits;
  const result = (Math.round(num * factor - 0.5) / factor).toFixed(digits);
  return isNumberNegative ? `-${result}` : result;
};

export const toFixedFloatUp = (num: number, digits: number): string => {
  const isNumberNegative = num < 0;
  num = Math.abs(num);
  const factor = 10 ** digits;
  const result = (Math.round(num * factor + 0.5) / factor).toFixed(digits);
  return isNumberNegative ? `-${result}` : result;
};

export const toFixedFloatWithUnits = (num: number, digits: number, units: string = '', convertEmptyNum: boolean = false): string => {
  return num || convertEmptyNum ? `${num.toFixed(digits)}${units}` : `${num}${units}`;
};

export const convertHexToRGBA = (hexCode: string, opacity: number = 1) => {
  let hex = hexCode.replace('#', '');

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

export const getGradientRange = (startColor: string, endColor: string, amountOfColorsInGradient: number): string[] => {
  return new Gradient()
    .setColorGradient(startColor, endColor)
    .setMidpoint(amountOfColorsInGradient < 2 ? 2 : amountOfColorsInGradient)
    .getColors();
};

export const getPercentage = (value: number, total: number, digits: number = 1): number => {
  if (total === 0) return 0; // Avoid division by zero
  const percentage = (value / total) * 100;
  return parseFloat(percentage.toFixed(digits));
};

export const getPercentageFormatted = (percentage: number, showZero: boolean = false, digits: number = 1): string => {
  return percentage || showZero
    ? `${Math.abs(percentage).toFixed(digits)}%`
    : '';
};

export const getNumberFormatted = (value: number, showZero: boolean = false, digits: number = 1, unit: string = 'pp'): string => {
  return value || showZero
    ? `${Math.abs(value).toFixed(digits)}${unit}`
    : '';
};

export const getDifferenceInPercentageFromCurrent = (current: number, previous: number, digits: number = 1): number => {
  let differencePercentage = 0;
  if (previous > 0) {
    const difference = current - previous;
    differencePercentage = getPercentage(difference, current, digits);
  }
  else if (current > 0) {
    differencePercentage = 100;
  }

  return differencePercentage;
};

export const getDifferenceInPercentageFormatted = (currentValue: number, previousValue: number): string => {
  const differencePercentage = getDifferenceInPercentageFromCurrent(currentValue, previousValue);
  return getPercentageFormatted(differencePercentage);
};

export const getAbsDifferenceValue = (currentValue: number, previousValue: number, unit: string = ""): string => {
  const difference = currentValue - previousValue;
  return difference ? `${Math.abs(difference).toFixed()}${unit}` : '';
};

export const getItemDefaultCopyName = (originalName: string, existingNames: string[]): string => {
  const defaultCopyName = `Copy of ${originalName}`;
  let itemsWithDefaultNameCount = 0;

  if (!existingNames || existingNames.length === 0) return defaultCopyName;

  const getItemCopyName = (itemName: string, defaultName: string, existingNames: string[]) => {
    const existingItemWithDefaultName = existingNames.find(
      name => name.toLowerCase() === itemName.toLowerCase());

    if (!existingItemWithDefaultName) return itemName;

    itemsWithDefaultNameCount++;
    return getItemCopyName(`${defaultName} ${itemsWithDefaultNameCount}`, defaultName, existingNames);
  };

  return getItemCopyName(defaultCopyName, defaultCopyName, existingNames);
};

export const normalizeStringValue = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  return value.trim();
};

export const normalizeStringNumberValue = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const normalizedValue = value.trim();
  const parsedValue = parseFloat(normalizedValue);

  return isNaN(parsedValue) ? undefined : parsedValue;
};

export const isDateReached = (date: Date | string | undefined): boolean => {
  if (!date) return false;

  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return false;

  return parsedDate.getTime() <= Date.now();
};

export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === "[object Object]";
};
